import React from 'react';

import { CustomFieldTypes } from 'common/customPostFields/Constants';
import translateString from 'common/i18n/translateString';
import Flex from 'common/ui/Flex';
import { Field, FieldContent, FieldError, FieldLabel } from 'common/uiV3/Field';
import { Select } from 'common/uiV3/Select';

import type { Board } from 'common/api/endpoints/boards';
import type { BoardField } from 'common/api/resources/postFields';
import type { FormErrors, PostForm } from 'common/post/CreatePostFormV2/utils';
import type { Option } from 'common/ui/common/select/SelectCommon';
import type { SelectBaseOption } from 'common/uiV3/Select/common';

type Props = {
  board: Board;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
};

const UncategorizedCategory: SelectBaseOption = {
  label: 'Uncategorized',
  value: '',
};

const toOption = (value?: string | string[] | null): Option | undefined => {
  if (!value || typeof value !== 'string') {
    return undefined;
  }

  return {
    label: value,
    value,
  };
};

const toOptions = (values?: string | string[] | null): Array<SelectBaseOption> => {
  if (!Array.isArray(values)) {
    return [];
  }

  return values.map((value) => ({
    label: value,
    value,
  }));
};

const PostFieldDropdowns = ({ board, onFormChange, form, errors }: Props) => {
  const boardFieldsWithOptions = board.boardFields
    .filter((boardField) => CustomFieldTypes[boardField.type]?.hasOptions)
    .sort((a, b) => a.order - b.order);

  return (
    <Flex className="flex gap-2 flex-wrap lg:flex-nowrap">
      <PostFieldCategory board={board} onFormChange={onFormChange} form={form} />
      {boardFieldsWithOptions.map((boardField) =>
        boardField.type === CustomFieldTypes.dropdown.name ? (
          <PostFieldDropdown
            key={boardField._id}
            boardField={boardField}
            onFormChange={onFormChange}
            form={form}
            errors={errors}
          />
        ) : (
          <PostFieldMultiSelect
            key={boardField._id}
            boardField={boardField}
            onFormChange={onFormChange}
            form={form}
            errors={errors}
          />
        ),
      )}
    </Flex>
  );
};

const PostFieldCategory = ({
  board,
  onFormChange,
  form,
}: {
  board: Board;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
}) => {
  if (board.categories.length === 0) {
    return null;
  }

  const categoryOptions = board.categories
    .map(
      (category): SelectBaseOption => ({
        label: `${category.parentID ? '\u2800' : ''}${translateString(category, 'name')}`,
        value: category._id,
      }),
    )
    .concat(UncategorizedCategory);

  const selectedCategory = categoryOptions.find((option) => option.value === form.categoryID);

  return (
    <Field className="flex-1">
      <FieldLabel>Category</FieldLabel>
      <FieldContent className="max-w-full lg:max-w-96">
        <Select
          onValueChange={(value) => onFormChange({ categoryID: value })}
          placeholder="Select a category"
          value={selectedCategory?.value}
          options={categoryOptions}
          fullWidth
          isSearchable={board.categories.length > 5}
        />
      </FieldContent>
    </Field>
  );
};

const PostFieldMultiSelect = ({
  boardField,
  onFormChange,
  form,
  errors,
}: {
  boardField: BoardField;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
}) => {
  const error = errors.customFieldValuesMap?.[boardField.customPostFieldID];

  return (
    <Field key={boardField._id} className="flex-1">
      <FieldLabel required={boardField.required}>{boardField.label}</FieldLabel>
      <FieldContent>
        <Select
          onValueChange={(values) =>
            onFormChange({
              customFieldValuesMap: {
                ...form.customFieldValuesMap,
                [boardField.customPostFieldID]: values.length ? values : null,
              },
            })
          }
          options={toOptions(boardField.options)}
          placeholder={boardField.placeholder}
          aria-invalid={!!error}
          aria-required={boardField.required}
          fullWidth
          isMultiSelect
          hideSelection
          maxVisible={2}
          allowClear={!boardField.required}
          value={toOptions(form.customFieldValuesMap[boardField.customPostFieldID]).map(
            (option) => option.value,
          )}
        />
        <FieldError errors={error ? [{ message: error }] : undefined} />
      </FieldContent>
    </Field>
  );
};

const PostFieldDropdown = ({
  boardField,
  onFormChange,
  form,
  errors,
}: {
  boardField: BoardField;
  onFormChange: (edits: Partial<PostForm>) => void;
  form: PostForm;
  errors: FormErrors;
}) => {
  if (!boardField.options) {
    return null;
  }

  const error = errors.customFieldValuesMap?.[boardField.customPostFieldID];

  return (
    <Field key={boardField._id} className="w-full lg:flex-1">
      <FieldLabel required={boardField.required}>{boardField.label}</FieldLabel>
      <FieldContent>
        <Select
          onValueChange={(value) =>
            onFormChange({
              customFieldValuesMap: {
                ...form.customFieldValuesMap,
                [boardField.customPostFieldID]: value || null,
              },
            })
          }
          fullWidth
          options={toOptions(boardField.options)}
          placeholder={boardField.placeholder}
          aria-invalid={!!error}
          aria-required={boardField.required}
          isSearchable={boardField.options.length > 5}
          allowClear={!boardField.required}
          value={toOption(form.customFieldValuesMap[boardField.customPostFieldID])?.value}
        />
        <FieldError errors={error ? [{ message: error }] : undefined} />
      </FieldContent>
    </Field>
  );
};

export default PostFieldDropdowns;
